import React from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs } from '../../components/Breadcrumbs';
import Button from '../../components/Button/Button';
import { FormField } from '../../components/FormField';
import { UsersList } from './UsersList';
import { Pagination } from '../../components/Pagination';
import { CustomSelect } from '../../components/Shared/CustomSelect';
import { useSetFilters } from '../../hooks/useSetFilters';
import { useQuery } from '../../utils/useQuery';
import Loading from '../../components/Loading';

import { useGetUsers } from '../../network';


export const UsersPage = () => {
    const setFilters = useSetFilters();
    const { t } = useTranslation();
    const query = useQuery();

    const page = parseInt(query.get('page') || '') || 1;
    const perPage = parseInt(query.get('per_page') || '') || 10;
    const storeId = parseInt(query.get('store') || '');
    const roleId = parseInt(query.get('role') || '');

    const { data = {}, isLoading } = useGetUsers();

    const stores = data.stores || [];
    const roles = data.roles || [];
    const users = data.users || [];

    // store select options
    const storesOptions = [
        { value: '', label: t('All stores') },
        ...stores.map((res) => ({
            label: res.name,
            value: res.id,
        })),
    ];

    // role select options
    const roleOptions = [
        { value: '', label: t('By Role') },
        ...roles.map((res) => ({
            label: res.name,
            value: res.id,
        })),
    ];

    // filtered users by store id
    const filteredUsersByStore = storeId ? users.filter((user) => user.restaurant_id === storeId) : users;

    // filtered users by role id
    const filteredUsersByRole = roleId ? filteredUsersByStore.filter((user) => user.role_id === roleId) : filteredUsersByStore;

    const numPages = Math.ceil(filteredUsersByRole.length / perPage || 1);
    const paginatedUsers = filteredUsersByRole.slice((page - 1) * perPage, page * perPage);

    // filters
    const setCurrentPage = (page) => setFilters({ page });
    const onChangePerPage = ({ value }) => setFilters({ per_page: value, page: 1 });
    const onChangeStoreId = ({ value }) => setFilters({ page: 1, store: value });
    const onChangeRoleId = ({ value }) => setFilters({ page: 1, role: value });

    const pages = [{ name: t('Teams'), path: '/chain/users' }];

    return (
        <div className="max-w-screen-lg">
            <Breadcrumbs pages={pages} />
            <div className="flex items-center justify-between gap-3">
                <div className="flex gap-3">
                    <FormField>
                        <CustomSelect className="text-left w-40" options={storesOptions} defaultValue={storesOptions[0]} onChange={onChangeStoreId} />
                    </FormField>
                    <FormField>
                        <CustomSelect className="text-left w-40" options={roleOptions} defaultValue={roleOptions[0]} isSearchable={false} onChange={onChangeRoleId} />
                    </FormField>
                </div>
                <Button isUpperCase={false} type="link" to="/chain/users/create" className="font-semibold" tracing="tracking-normal">
                    {t('Add user')}
                </Button>
            </div>
            <Loading loading={isLoading}>
                <UsersList users={paginatedUsers} roles={roleOptions} stores={storesOptions} />
            </Loading>

            <div className="flex justify-center items-center pt-3 pb-3">
                <Pagination className="ml-12" numPages={numPages} currentPage={page} setCurrentPage={setCurrentPage} />
                <FormField className="ml-5">
                    <CustomSelect
                        isSearchable={false}
                        defaultValue={{ label: perPage, value: perPage }}
                        onChange={onChangePerPage}
                        menuPlacement="top"
                        options={[
                            { label: 5, value: 5 },
                            {
                                label: 10,
                                value: 10,
                            },
                            { label: 20, value: 20 },
                            {
                                label: 50,
                                value: 50,
                            },
                            { label: 100, value: 100 },
                        ]}
                    />
                </FormField>
            </div>
        </div>
    );
};
